import React, { useState } from 'react';
import { Button, TextField, Box, Typography, Alert, CircularProgress } from '@mui/material';
import { Code, Transform, ContentCopy, Clear } from '@mui/icons-material';
import './JsonTransformer.css';
import { apiFetch, buildAuthHeaders } from '../auth/apiFetch';

interface JsonTransformerProps {
  onTransform?: (inputJson: string) => Promise<string>;
}

export const JsonTransformer: React.FC<JsonTransformerProps> = ({ onTransform }) => {
  const [inputJson, setInputJson] = useState('');
  const [outputJson, setOutputJson] = useState('');
  const [isTransforming, setIsTransforming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isValidJson, setIsValidJson] = useState(true);

  const validateJson = (jsonString: string): boolean => {
    if (!jsonString.trim()) return true;
    try {
      JSON.parse(jsonString);
      return true;
    } catch {
      return false;
    }
  };

  const formatJson = (jsonString: string): string => {
    try {
      return JSON.stringify(JSON.parse(jsonString), null, 2);
    } catch {
      return jsonString;
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;
    setInputJson(value);
    setIsValidJson(validateJson(value));
    setError(null);
  };

  const handleTransform = async () => {
    if (!inputJson.trim()) {
      setError('Please enter JSON to transform');
      return;
    }

    if (!isValidJson) {
      setError('Invalid JSON format');
      return;
    }

    setIsTransforming(true);
    setError(null);

    try {
      if (onTransform) {
        const result = await onTransform(inputJson);
        setOutputJson(formatJson(result));
      } else {
        // Call the transform API endpoint
        const response = await apiFetch('/transform', {
          method: 'POST',
          headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
          body: inputJson,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        
        if (result.status === 'success') {
          setOutputJson(formatJson(JSON.stringify(result.transformedPayload)));
        } else {
          throw new Error(result.message || 'Transformation failed');
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Transformation failed');
      setOutputJson('');
    } finally {
      setIsTransforming(false);
    }
  };

  const handleCopyOutput = () => {
    navigator.clipboard.writeText(outputJson);
  };

  const handleClear = () => {
    setInputJson('');
    setOutputJson('');
    setError(null);
    setIsValidJson(true);
  };

  const handlePasteExample = () => {
    const exampleJson = {
      "payloadVersion": 0.1,
      "to": "919999999999",
      "wabaNumber": "91999999XXXX",
      "msgId": "25596363|1639561857|20220227|25596363",
      "type": "text",
      "isTemplate": false,
      "text": {
        "body": "Hello, this is a test message"
      }
    };
    setInputJson(JSON.stringify(exampleJson, null, 2));
    setIsValidJson(true);
  };

  return (
    <div className="json-transformer">
      <div className="transformer-header">
        <Typography variant="h5" component="h2" className="transformer-title">
          <Code className="title-icon" />
          JSON Payload Transformer
        </Typography>
        <Typography variant="body2" color="text.secondary" className="transformer-subtitle">
          Paste your JSON payload and transform it for troubleshooting
        </Typography>
      </div>

      <div className="transformer-content">
        <div className="input-section">
          <div className="section-header">
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>CT Input Payload</Typography>
            <div className="section-actions">
              <Button
                size="small"
                startIcon={<ContentCopy />}
                onClick={handlePasteExample}
                variant="outlined"
              >
                Example
              </Button>
              <Button
                size="small"
                startIcon={<Clear />}
                onClick={handleClear}
                variant="outlined"
                color="secondary"
              >
                Clear
              </Button>
            </div>
          </div>
          
          <TextField
            multiline
            rows={12}
            fullWidth
            value={inputJson}
            onChange={handleInputChange}
            placeholder="Paste your JSON payload here..."
            variant="outlined"
            error={!isValidJson}
            helperText={!isValidJson ? 'Invalid JSON format' : `${inputJson.length} characters`}
            className="json-input"
            InputProps={{
              style: { fontFamily: 'Monaco, Consolas, "Courier New", monospace' }
            }}
          />
        </div>

        <div className="transform-section">
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={isTransforming ? <CircularProgress size={20} /> : <Transform />}
            onClick={handleTransform}
            disabled={!inputJson.trim() || !isValidJson || isTransforming}
            className="transform-button"
          >
            {isTransforming ? 'Transforming...' : 'Transform'}
          </Button>
        </div>

        <div className="output-section">
          <div className="section-header">
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Transformed Onex WA-API Payload</Typography>
            {outputJson && (
              <Button
                size="small"
                startIcon={<ContentCopy />}
                onClick={handleCopyOutput}
                variant="outlined"
              >
                Copy
              </Button>
            )}
          </div>
          
          <TextField
            multiline
            rows={12}
            fullWidth
            value={outputJson}
            placeholder="Transformed JSON will appear here..."
            variant="outlined"
            InputProps={{
              readOnly: true,
              style: { fontFamily: 'Monaco, Consolas, "Courier New", monospace' }
            }}
            className="json-output"
          />
        </div>
      </div>

      {error && (
        <Alert severity="error" className="error-alert">
          {error}
        </Alert>
      )}
    </div>
  );
};
